/**
 * WhatYouStudyCard — the deepened "What you'll study" narrative
 * block from the Feb 2026 course-page redesign.
 *
 * Layout (top → bottom):
 *   1. Editorial paragraph — copy precedence:
 *        a) `degree.professor_blurb`  (hand-seeded editorial voice)
 *        b) `aiSummary`               (existing Professor writeup,
 *                                       no new LLM spend)
 *        c) design-guidelines empty state + EmptyCellHelpLink
 *   2. Signals row — five sparse signals rendered as pills, each
 *      hidden individually when its source field is missing:
 *        · Assessment split      (`curriculum.assessment_split`)
 *        · Contact hours / week  (`curriculum.contact_hours_per_week`)
 *        · Optional module count (`curriculum.optional_module_count`)
 *        · Study-abroad          (`curriculum.study_abroad_available`)
 *        · Teaching style        (`curriculum.teaching_style`)
 *   3. Year 1/2/3 module strips — from
 *      `degree.curriculum.compulsory_modules`.
 *
 * Every signal source lives under `curriculum.*` so ops can seed
 * them independently of the top-level `professor_blurb` field.
 */
import { BookOpen, Compass, Globe2, GraduationCap, Timer, Layers } from "lucide-react";
// Feb 2026 — EmptyCellHelpLink removed after the "syllabus being updated"
// placeholder was dropped; the card hides itself entirely when it has
// no data, so the help link has nowhere to live.

function _pctSplitToPills(split) {
  // Accepts either an object `{essay: 40, exam: 30, project: 30}` or
  // an array `[{label:"Essay", value:40}, …]`. Returns
  // `[{label, value}]` sorted desc by value.
  if (!split) return [];
  let pairs;
  if (Array.isArray(split)) {
    pairs = split
      .filter((s) => s && typeof s.value === "number")
      .map((s) => ({ label: String(s.label || "").trim(), value: s.value }));
  } else if (typeof split === "object") {
    pairs = Object.entries(split)
      .filter(([, v]) => typeof v === "number" && v > 0)
      .map(([k, v]) => ({
        label: k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, " "),
        value: v,
      }));
  } else {
    return [];
  }
  return pairs.filter((p) => p.label).sort((a, b) => b.value - a.value).slice(0, 4);
}

function SignalPill({ Icon, label, value, testid, title }) {
  return (
    <div
      data-testid={testid}
      title={title}
      className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[12px]"
    >
      <Icon
        className="h-3.5 w-3.5 text-[#4C1D95]"
        strokeWidth={2.25}
        aria-hidden
      />
      <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
        {label}
      </span>
      <span className="font-medium text-stone-800">{value}</span>
    </div>
  );
}

/**
 * KIS/Discover Uni fallback: when we don't have an explicit
 * `curriculum.assessment_split` % breakdown (HESA dropped it from the
 * 2024/25 KIS release), surface the NSS "Assessment & feedback"
 * satisfaction score from `degree.outcomes.nss_scores.feedback`.
 *
 * Returns `{ pct, sample }` when the number is trustworthy
 * (feedback: 0..100 and sample_size >= 23 — same threshold the
 * KIS parser uses to classify a course as `verified`).
 * Returns null otherwise so the pill stays silent instead of
 * flashing a noisy single-digit sample.
 */
function _nssAssessmentSatisfaction(degree) {
  const nss = degree?.outcomes?.nss_scores;
  if (!nss || typeof nss !== "object") return null;
  const pct = nss.feedback;
  const sample = nss.sample_size;
  if (typeof pct !== "number" || pct < 0 || pct > 100) return null;
  if (typeof sample !== "number" || sample < 23) return null;
  return { pct, sample };
}

export default function WhatYouStudyCard({ degree }) {
  // Feb 2026: previously this component fell back to the AI-generated
  // `interpretation.summary` string when its dedicated `professor_blurb`
  // was empty — but the same LLM paragraph is ALREADY rendered by
  // `AIInterpretationSummary` ("The professor · at a glance") near the
  // top of the page, which produced a visible duplicate on ~every
  // course. We now render the seeded editorial blurb only, and let the
  // signals/year strips carry the section when no editorial copy exists.
  const blurb =
    (degree?.professor_blurb && degree.professor_blurb.trim()) || "";

  const curriculum = degree?.curriculum || {};

  // ── Signals row ────────────────────────────────────────────────
  const splitPills = _pctSplitToPills(curriculum.assessment_split);
  // KIS fallback: NSS "Assessment & feedback" theme satisfaction %,
  // populated by the discover_uni CSV overlay on 63 flagship UK
  // courses (Feb 2026). Silent when curriculum.assessment_split is
  // present so the % breakdown wins whenever we have it.
  const nssAssessment =
    splitPills.length === 0 ? _nssAssessmentSatisfaction(degree) : null;
  const contactHours = curriculum.contact_hours_per_week;
  const optionalCount = curriculum.optional_module_count;
  // study_abroad_available: true / false / null. False = "not offered";
  // null = "unknown, silent".
  const studyAbroad = curriculum.study_abroad_available;
  const teachingStyle = (curriculum.teaching_style || "").trim();

  const hasAnySignal =
    splitPills.length > 0 ||
    nssAssessment !== null ||
    contactHours != null ||
    optionalCount != null ||
    studyAbroad != null ||
    teachingStyle.length > 0;

  // ── Year strips ────────────────────────────────────────────────
  const modulesByYear = curriculum.compulsory_modules || null;
  const yearEntries = modulesByYear
    ? Object.entries(modulesByYear).filter(
        ([, mods]) => Array.isArray(mods) && mods.length > 0
      )
    : [];

  // Hide the whole card when nothing meaningful is available — better
  // than a bare "syllabus being updated" placeholder that duplicates
  // the "help us improve this section" affordance found elsewhere.
  const hasAnything =
    blurb.length > 0 || hasAnySignal || yearEntries.length > 0;
  if (!hasAnything) return null;

  return (
    <section
      data-testid="what-you-study-card"
      className="mt-10 rounded-2xl border border-stone-200 bg-white/70 backdrop-blur-sm px-6 py-6 sm:px-8 sm:py-7"
    >
      <div className="flex items-center gap-2 mb-4">
        <BookOpen
          className="h-4 w-4 text-[#4C1D95]"
          strokeWidth={2.25}
          aria-hidden
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4C1D95]">
          What you'll study
        </span>
      </div>

      {blurb.length > 0 && (
        <p
          data-testid="what-you-study-blurb"
          className="font-editorial text-lg sm:text-xl leading-[1.65] text-stone-800"
        >
          {blurb}
        </p>
      )}

      {hasAnySignal && (
        <div
          data-testid="what-you-study-signals"
          className="mt-5 flex flex-wrap gap-2"
        >
          {splitPills.length > 0 && (
            <SignalPill
              Icon={Layers}
              label="Assessment"
              value={splitPills.map((p) => `${p.value}% ${p.label.toLowerCase()}`).join(" · ")}
              testid="what-you-study-assessment"
            />
          )}
          {nssAssessment && (
            <SignalPill
              Icon={Layers}
              label="Assessment satisfaction"
              value={`${nssAssessment.pct}% (NSS · n=${nssAssessment.sample})`}
              testid="what-you-study-assessment-nss"
              title="National Student Survey — % of graduating students positive about how they were assessed and given feedback. HESA/OfS dropped the coursework/exam % breakdown from the 2024/25 KIS release; NSS satisfaction is our closest available signal."
            />
          )}
          {contactHours != null && (
            <SignalPill
              Icon={Timer}
              label="Contact hours"
              value={`${contactHours}/wk`}
              testid="what-you-study-contact-hours"
            />
          )}
          {optionalCount != null && (
            <SignalPill
              Icon={GraduationCap}
              label="Optional modules"
              value={`${optionalCount} to pick from`}
              testid="what-you-study-optional-count"
            />
          )}
          {studyAbroad != null && (
            <SignalPill
              Icon={Globe2}
              label="Study abroad"
              value={studyAbroad ? "Available" : "Not offered"}
              testid="what-you-study-study-abroad"
            />
          )}
          {teachingStyle.length > 0 && (
            <SignalPill
              Icon={Compass}
              label="Teaching style"
              value={teachingStyle}
              testid="what-you-study-teaching-style"
            />
          )}
        </div>
      )}

      {yearEntries.length > 0 && (
        <div
          data-testid="what-you-study-year-strips"
          className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
          {yearEntries.slice(0, 3).map(([yearLabel, mods], idx) => (
            <div
              key={yearLabel}
              data-testid={`what-you-study-year-${idx}`}
              className="rounded-xl border border-stone-200 bg-[#FAF7F1] px-4 py-3"
            >
              <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-2">
                {yearLabel}
              </div>
              <ul className="space-y-1 text-sm text-stone-800 leading-snug">
                {mods.slice(0, 4).map((m, i) => (
                  <li key={i}>· {m}</li>
                ))}
                {mods.length > 4 && (
                  <li className="text-[11px] text-stone-500 italic">
                    +{mods.length - 4} more
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
